import { addDays } from "./format";

/** How far `timeZone`'s clock is ahead of UTC at the moment `at`, in milliseconds. */
function offsetMs(timeZone: string, at: Date): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(at)
      .map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/** The moment a wall-clock date and time ("2026-10-04", "20:00") happen in `timeZone`. */
export function zonedMoment(date: string, time: string, timeZone: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  const [h, min] = time.split(":").map(Number);
  const wall = Date.UTC(y!, m! - 1, d!, h!, min!);
  // Twice: the second pass corrects for a daylight-saving change between the guess and the answer.
  const first = wall - offsetMs(timeZone, new Date(wall));
  return new Date(wall - offsetMs(timeZone, new Date(first)));
}

/**
 * When whole-day pauses of `date` close: the evening before, at the brand's day-pause cutoff, in its zone
 * (DECISIONS.md §6; each meal closes at its own slot's cutoff). 2026-10-05 with 20:00 in India → 4 Oct, 8 pm IST.
 */
export const dayPauseClosesAt = (date: string, dayPauseCutoffTime: string, timeZone: string): Date =>
  zonedMoment(addDays(date, -1), dayPauseCutoffTime, timeZone);

/** 0 = Sunday … 6 = Saturday, as the API numbers weekdays. */
export const weekdayOf = (date: string): number => new Date(`${date}T00:00:00Z`).getUTCDay();

/** The Monday on or before `date` (weeks run Monday to Sunday). */
export const mondayOf = (date: string): string => addDays(date, -((weekdayOf(date) + 6) % 7));
